import {
  analyticsOutline,
  giftOutline,
  lockOpen,
  peopleCircle,
  peopleCircleOutline,
  albums,
  calendarOutline,
  settings,
  beerOutline,
  bookmarksOutline,
  hammer,
  shieldCheckmarkOutline,
  trophyOutline,
  clipboardOutline,
  cashOutline,
  speedometerOutline,
  keyOutline,
  ticketOutline,
} from "ionicons/icons";
import { matchPath } from "react-router-dom";
import { AdminModuleKey } from "../../Common/models/adminRole";
import { ENVIRONMENTS } from "../../Common/config";
import * as routes from "./routes";

const ADMIN_HOME_STATIC_PATHS = [
  routes.HOME,
  routes.ADMIN_NO_ACCESS,
  routes.ADMIN_ROLES,
];

export type AdminNavIcon = string | "locker" | "discounts";

export type AdminRouteEnvGate = "shop" | "test" | "qaTools";

export interface AdminHomeModuleRoute {
  module: AdminModuleKey;
  label: string;
  primaryPath: string;
  matchPaths: string[];
  pathPrefixes?: string[];
  navIcon: AdminNavIcon;
  enabledBy?: AdminRouteEnvGate;
  includeInNav?: boolean;
}

export interface AdminHomeSuperAdminNavRoute {
  label: string;
  path: string;
  navIcon: AdminNavIcon;
}

export interface AdminNavItem {
  label: string;
  path: string;
  icon: AdminNavIcon;
  module?: AdminModuleKey;
  requireSuperAdmin?: boolean;
}

export const TEST_ENABLED =
  [
    ENVIRONMENTS.local,
    ENVIRONMENTS.development,
    ENVIRONMENTS.staging,
  ].indexOf(import.meta.env.MODE) !== -1;

/** Ordered module route registry — single source for nav, permissions, and path matching */
export const ADMIN_HOME_MODULE_ROUTES: AdminHomeModuleRoute[] = [
  {
    module: "ANALYTICS",
    label: "Analytics",
    primaryPath: routes.ADMIN_ANALYTICS,
    matchPaths: [routes.ADMIN_ANALYTICS, routes.REALTIME_ANALYTICS],
    navIcon: analyticsOutline,
  },
  {
    module: "KPI_ANALYTICS",
    label: "KPI Analytics",
    primaryPath: routes.KPI_ANALYTICS,
    matchPaths: [routes.KPI_ANALYTICS],
    navIcon: speedometerOutline,
  },
  {
    module: "USERS",
    label: "Users",
    primaryPath: routes.ADMIN_USERS,
    matchPaths: [routes.ADMIN_USERS, routes.ADMIN_USER_DETAIL],
    pathPrefixes: ["/home/view-user"],
    navIcon: peopleCircleOutline,
  },
  {
    module: "METRICS",
    label: "Metrics",
    primaryPath: routes.ADMIN_PRIMARY_METRICS,
    matchPaths: [routes.ADMIN_PRIMARY_METRICS],
    navIcon: albums,
  },
  {
    module: "GIVEAWAYS",
    label: "Giveaways",
    primaryPath: routes.ADMIN_GIVEAWAYS,
    matchPaths: [routes.ADMIN_GIVEAWAYS],
    navIcon: "locker",
  },
  {
    module: "SEASONS",
    label: "Seasons",
    primaryPath: routes.ADMIN_SEASONS,
    matchPaths: [routes.ADMIN_SEASONS],
    navIcon: trophyOutline,
  },
  {
    module: "CALENDAR",
    label: "Calendar",
    primaryPath: routes.ADMIN_CALENDAR,
    matchPaths: [routes.ADMIN_CALENDAR],
    navIcon: calendarOutline,
  },
  {
    module: "SURVEY",
    label: "Survey",
    primaryPath: routes.ADMIN_SURVEY,
    matchPaths: [routes.ADMIN_SURVEY],
    navIcon: clipboardOutline,
  },
  {
    module: "CASH",
    label: "Cash",
    primaryPath: routes.ADMIN_CASH_DISBURSEMENT,
    matchPaths: [routes.ADMIN_CASH_DISBURSEMENT],
    navIcon: cashOutline,
  },
  {
    module: "TESTAMENTS",
    label: "Testaments",
    primaryPath: routes.ADMIN_WINNERS_TESTAMENTS,
    matchPaths: [routes.ADMIN_WINNERS_TESTAMENTS],
    navIcon: beerOutline,
  },
  {
    module: "DISCOUNTS",
    label: "Discounts",
    primaryPath: routes.ADMIN_DISCOUNTS,
    matchPaths: [routes.ADMIN_DISCOUNTS],
    navIcon: "discounts",
  },
  {
    module: "PROMOS",
    label: "Promos",
    primaryPath: routes.ADMIN_PROMOS,
    matchPaths: [routes.ADMIN_PROMOS],
    navIcon: giftOutline,
  },
  {
    module: "REDEEM_CODES",
    label: "Redeem Codes",
    primaryPath: routes.ADMIN_REDEEM_CODES,
    matchPaths: [routes.ADMIN_REDEEM_CODES],
    navIcon: ticketOutline,
  },
  {
    module: "PROMOTIONS",
    label: "Promotion",
    primaryPath: routes.ADMIN_PROMOTIONS,
    matchPaths: [routes.ADMIN_PROMOTIONS],
    navIcon: bookmarksOutline,
  },
  {
    module: "SHOP",
    label: "Shop",
    primaryPath: routes.ADMIN_SHOP,
    matchPaths: [
      routes.ADMIN_SHOP,
      routes.ADMIN_SHOP_PRODUCT_CREATE,
      routes.ADMIN_SHOP_PRODUCT_DETAIL,
      routes.ADMIN_SHOP_ORDER_DETAIL,
      routes.ADMIN_SHOP_CART_UPSELL,
    ],
    pathPrefixes: [
      "/home/admin-shop/product",
      "/home/admin-shop/order",
      "/home/admin-shop/upsell",
    ],
    navIcon: "discounts",
    enabledBy: "shop",
  },
  {
    module: "ADMIN_USERS",
    label: "Admin Users",
    primaryPath: routes.ADMIN_ADMIN_USERS,
    matchPaths: [routes.ADMIN_ADMIN_USERS],
    navIcon: peopleCircle,
  },
  {
    module: "DATA_SECURITY_FRAUD",
    label: "Data Security",
    primaryPath: routes.ADMIN_DATA_SECURITY,
    matchPaths: [routes.ADMIN_DATA_SECURITY],
    navIcon: shieldCheckmarkOutline,
  },
  {
    module: "SETTINGS",
    label: "Settings",
    primaryPath: routes.ADMIN_SETTINGS,
    matchPaths: [routes.ADMIN_SETTINGS],
    navIcon: settings,
  },
  {
    module: "TEST",
    label: "TEST",
    primaryPath: routes.PLAYGROUND,
    matchPaths: [routes.PLAYGROUND],
    navIcon: lockOpen,
    enabledBy: "test",
  },
  {
    module: "QA_TEST_TOOLS",
    label: "QA TEST TOOLS",
    primaryPath: routes.QA_TESTER_TOOLS,
    matchPaths: [routes.QA_TESTER_TOOLS],
    navIcon: hammer,
    enabledBy: "qaTools",
  },
];

export const ADMIN_HOME_SUPER_ADMIN_NAV_ROUTE: AdminHomeSuperAdminNavRoute = {
  label: "Admin Roles",
  path: routes.ADMIN_ROLES,
  navIcon: keyOutline,
};

/** Ordered paths for back/forward navigation direction in the admin shell */
export const ADMIN_HOME_ROUTE_ORDER: string[] = [
  routes.ADMIN_ANALYTICS,
  routes.REALTIME_ANALYTICS,
  routes.KPI_ANALYTICS,
  routes.ADMIN_USERS,
  routes.ADMIN_PRIMARY_METRICS,
  routes.ADMIN_GIVEAWAYS,
  routes.ADMIN_SEASONS,
  routes.ADMIN_CALENDAR,
  routes.ADMIN_SURVEY,
  routes.ADMIN_CASH_DISBURSEMENT,
  routes.ADMIN_WINNERS_TESTAMENTS,
  routes.ADMIN_DISCOUNTS,
  routes.ADMIN_PROMOS,
  routes.ADMIN_REDEEM_CODES,
  routes.ADMIN_PROMOTIONS,
  routes.ADMIN_SHOP,
  routes.ADMIN_SHOP_PRODUCT_DETAIL,
  routes.ADMIN_SHOP_ORDER_DETAIL,
  routes.ADMIN_SHOP_CART_UPSELL,
  routes.ADMIN_ADMIN_USERS,
  routes.ADMIN_ROLES,
  routes.ADMIN_DATA_SECURITY,
  routes.ADMIN_SETTINGS,
  routes.PLAYGROUND,
];

export const isEnvGateEnabled = (
  gate: AdminRouteEnvGate | undefined,
  options: {
    shopEnabled: boolean;
    testEnabled: boolean;
    qaToolsEnabled: boolean;
  },
): boolean => {
  if (!gate) {
    return true;
  }
  if (gate === "shop") {
    return options.shopEnabled;
  }
  if (gate === "test") {
    return options.testEnabled;
  }
  return options.qaToolsEnabled;
};

export const normalizeAdminPath = (pathname: string): string => {
  if (pathname.startsWith("/home/admin-shop/order/")) {
    return routes.ADMIN_SHOP_ORDER_DETAIL;
  }
  if (pathname.startsWith("/home/admin-shop/product/")) {
    return routes.ADMIN_SHOP_PRODUCT_DETAIL;
  }
  if (pathname.startsWith("/home/admin-shop/upsell/")) {
    return routes.ADMIN_SHOP_CART_UPSELL;
  }
  return pathname;
};

const pathMatchesParamRoute = (
  pathname: string,
  routePath: string,
): boolean => {
  const match = matchPath({ path: routePath, end: false }, pathname);
  if (!match?.params) {
    return false;
  }

  return Object.values(match.params).every((value) => Boolean(value));
};

export const pathMatchesAdminPrefix = (
  pathname: string,
  prefix: string,
): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`);

const pathHasAdminPrefixParam = (pathname: string, prefix: string): boolean => {
  if (!pathname.startsWith(`${prefix}/`)) {
    return false;
  }

  const remainingPath = pathname.slice(prefix.length + 1);
  return Boolean(remainingPath.split("/")[0]);
};

/** Whether a pathname maps to a registered admin home route (including dynamic segments) */
export const isRecognizedAdminHomePath = (pathname: string): boolean => {
  if (ADMIN_HOME_STATIC_PATHS.includes(pathname)) {
    return true;
  }

  for (const { matchPaths, pathPrefixes } of ADMIN_HOME_MODULE_ROUTES) {
    if (
      pathPrefixes?.some((prefix) => pathHasAdminPrefixParam(pathname, prefix))
    ) {
      return true;
    }

    for (const routePath of matchPaths) {
      if (routePath.includes(":")) {
        if (pathMatchesParamRoute(pathname, routePath)) {
          return true;
        }
        continue;
      }

      if (pathname === routePath) {
        return true;
      }
    }
  }

  return false;
};

export const buildAdminNavItems = (
  canView: (module: AdminModuleKey) => boolean,
  options: {
    isSuperAdmin: boolean;
    shopEnabled: boolean;
    testEnabled: boolean;
    qaToolsEnabled: boolean;
  },
): AdminNavItem[] => {
  const items: AdminNavItem[] = [];

  for (const route of ADMIN_HOME_MODULE_ROUTES) {
    if (route.includeInNav === false) {
      continue;
    }
    if (!isEnvGateEnabled(route.enabledBy, options)) {
      continue;
    }

    items.push({
      label: route.label,
      path: route.primaryPath,
      icon: route.navIcon,
      module: route.module,
    });

    if (route.module === "ADMIN_USERS" && options.isSuperAdmin) {
      items.push({
        label: ADMIN_HOME_SUPER_ADMIN_NAV_ROUTE.label,
        path: ADMIN_HOME_SUPER_ADMIN_NAV_ROUTE.path,
        icon: ADMIN_HOME_SUPER_ADMIN_NAV_ROUTE.navIcon,
        requireSuperAdmin: true,
      });
    }
  }

  return items.filter((item) => {
    if (item.requireSuperAdmin) {
      return options.isSuperAdmin;
    }
    if (!item.module) {
      return true;
    }
    return canView(item.module);
  });
};
